import React, { useState, useEffect } from 'react';
import PageHeader from '../PageHeader';
import CryptoJS from 'crypto-js';

const Article: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
    <div className={`mb-8 ${className}`}>
        {title && <h4 className="text-lg font-semibold text-slate-800 mb-3">{title}</h4>}
        <div className="text-slate-600 space-y-3">
            {children}
        </div>
    </div>
);

const SubArticle: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
    <div className={`mt-4 ${className}`}>
        <h5 className="font-semibold text-slate-700 mb-2">{title}</h5>
        <div className="text-slate-600 space-y-3">
            {children}
        </div>
    </div>
);

const ListItem: React.FC<{ num: string; children: React.ReactNode }> = ({ num, children }) => (
    <div className="flex">
        <span className="w-10 flex-shrink-0 font-medium text-slate-700">{num}</span>
        <div className="flex-1">{children}</div>
    </div>
);

const SubListItem: React.FC<{ char: string; children: React.ReactNode }> = ({ char, children }) => (
     <div className="flex ml-10 mt-2">
        <span className="w-6 flex-shrink-0">{`(${char})`}</span>
        <p className="flex-1">{children}</p>
    </div>
);

const CentreList = [
    { id: 0, centreName: "Unassigned", value: 0 },
    { id: 1, centreName: "TM Gasing 136", value: 1 },
    { id: 2, centreName: "TM Sri Aman", value: 2 },
    { id: 3, centreName: "TM Gasing 60", value: 3 },
    { id: 4, centreName: "Komune Care", value: 4 },
    { id: 5, centreName: "TPR Penang", value: 5 },
    { id: 6, centreName: "HomeCare", value: 6 },
    { id: 7, centreName: "TM Ritchie", value: 7 },
    { id: 8, centreName: "Demo Company (Xero Development)", value: 8 },
    { id: 9, centreName: "Angsana Home", value: 9 },
    { id: 10, centreName: "Ara Woods", value: 10 },
    { id: 11, centreName: "Jaya One Daycare", value: 11 },
    { id: 12, centreName: "Academy Care", value: 12 },
    { id: 13, centreName: "Care Collective", value: 13 },
    { id: 14, centreName: "Care Lux (Oakwood project)", value: 14 },
    { id: 15, centreName: "AmpangPuri", value: 15 }
];

const fileMapping: Record<string, string> = {
    "AmpangPuri": "APP_service_agreement_v1.0.html",
    "Ara Woods": "ARW_service_agreement_v1.0.html",
    "Angsana Home": "ASN_service_agreement_v1.0.html",
    "Care Collective": "CCK_service_agreement_v1.0.html",
    "TM Gasing 60": "G58_service_agreement_v1.0.html",
    "TM Gasing 136": "G136_service_agreement_v1.0.html",
    "Jaya One Daycare": "JOC_service_agreement_v1.0.html",
    "TM Sri Aman": "SA_service_agreement_v1.0.html",
    "TPR Penang": "TPR_service_agreement_v1.0.html"
};

interface ServiceAgreementPageProps {
  onContinue: () => void;
  isPrintView?: boolean;
}

const ServiceAgreementPage: React.FC<ServiceAgreementPageProps> = ({ onContinue, isPrintView = false }) => {
  const [saHtml, setSaHtml] = useState<string | null>(null);
  const [isLoadingHtml, setIsLoadingHtml] = useState(true);

  useEffect(() => {
      const fetchSaHtml = async () => {
          const params = new URLSearchParams(window.location.search);
          const rawCentreParam = params.get('centre');
          const centreParam = rawCentreParam ? rawCentreParam.replace(/ /g, '+') : null;
          let centreName = 'Care Concierge'; // Default fallback

          if (centreParam) {
              try {
                  const encKey = "CareConcierge";
                  const bytes = CryptoJS.AES.decrypt(centreParam, encKey);
                  const decryptedId = bytes.toString(CryptoJS.enc.Utf8);
                  const match = CentreList.find(item => item.value === Number(decryptedId));
                  if (match) centreName = match.centreName;
              } catch (error) {
                  console.error("Failed to decrypt centre ID:", error);
              }
          }

          const fileName = fileMapping[centreName];
          if (fileName) {
              try {
                  const r = await fetch(`/rc-new/service_agreement/${fileName}`);
                  if (r.ok) {
                      const html = await r.text();
                      setSaHtml(html);
                  } else {
                      console.error('Failed statically fetching SA agreement HTML:', r.status);
                  }
              } catch (e) {
                  console.error('Failed to load specific Service agreement:', e);
              }
          }
          setIsLoadingHtml(false);
      };
      fetchSaHtml();
  }, []);

  const saIframeRef = React.useRef<HTMLIFrameElement>(null);

  const resizeIframe = () => {
    if (saIframeRef.current && saIframeRef.current.contentWindow) {
      const bodyHeight = saIframeRef.current.contentWindow.document.documentElement.scrollHeight;
      saIframeRef.current.style.height = `${bodyHeight + 20}px`;
    }
  };

  useEffect(() => {
      const t = setTimeout(resizeIframe, 300);
      return () => clearTimeout(t);
  }, [saHtml]);

  const NextButton = (
    <button
      onClick={onContinue}
      className="w-full sm:w-auto px-8 py-3 bg-brand-primary text-white font-bold rounded-lg shadow-md hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200"
    >
      Next
    </button>
  );

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Service Agreement"
        subtitle="This agreement outlines the terms and conditions of residency and care."
        action={NextButton}
        isPrintView={isPrintView}
      />
      
      {isLoadingHtml ? (
          <div className="flex justify-center my-8">
              <span className="text-slate-400">Loading agreement...</span>
          </div>
      ) : saHtml ? (
          <div className="w-full mb-6 relative">
              {/* Ensure no styles leak, but native-like height adaptation */}
              <iframe 
                  ref={saIframeRef}
                  onLoad={resizeIframe}
                  srcDoc={saHtml} 
                  className="w-full border-0" 
                  scrolling="no"
                  title="Service Agreement"
              />
          </div>
      ) : (
        <div className="prose prose-slate max-w-full prose-sm sm:prose-base prose-p:leading-relaxed">
        <Article title="1.0 Parties">
            <ListItem num="1.1">
                Komune Care Centre Sdn Bhd (Company No.: -202101014451), a company incorporated in Malaysia with its registered office at A-1-2, Block A, Jaya One, Jalan University, 46200, Petaling Jaya, Selangor, Malaysia (the “Provider”) on one part; and
            </ListItem>
            <ListItem num="1.2">
                The party whose name and description as stipulated in Section 2 of Schedule 1 (the “Resident”) of other part; and
            </ListItem>
            <ListItem num="1.3">
                The party whose name and description as stipulated in Section 3 of Schedule 1 (the “Surety”) of other part.
            </ListItem>
            <p className="mt-3">(The Provider, the Resident and the Surety shall collectively be referred to as the “Parties” and each as “Party”.)</p>
        </Article>

        <Article title="2.0 Services">
             <ListItem num="2.1">
                In consideration of the payment of the Security Deposit (as defined hereinafter), the Service Fee (as defined hereinafter) and the performance and observation of the terms and conditions of this Agreement by the Resident and the Surety (collectively referred to as the “Customer”), the Provider hereby agrees to provide and the Customer hereby accepts the services as stipulated in Section 4 of Schedule 1 (“Services”) and the license for the Resident to occupy the room as stipulated in Section 5 of Schedule 1 (“Room”) at Komune Living and Wellness, 21, Jalan Tasik Permaisuri 2, Bandar Tun Razak, 56000 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur (“Premises”) upon the terms and subject to the covenants and provisions of this agreement ("this Agreemen t").
            </ListItem>
            <ListItem num="2.2">
                This Agreement shall be valid for a period as stipulated in Section 6 of Schedule 1 (“Term”) commencing from the date as stipulated in Section 7 of Schedule 1 (“Commencement Date”). This Agreement shall be automatically renewed unless otherwise terminated or agreed in writing by the Parties.
            </ListItem>
        </Article>

        <Article title="3.0 Medical Declaration">
            <p>The Customer shall declare and provide the medical history and condition of the Resident upon execution of this Agreement. In this regard, the Customer warrants that all information provided by the Customer to the Provider shall be deemed to be the latest, true and accurate information without any omissions by the Customer in assessing the Resident's health condition. For the avoidance of doubt, the Provider shall not be obliged to verify the accuracy of any such information provided by the Customer and the Provider shall not be held liable in any manner and the Customer shall indemnify and keep the Provider indemnified against any losses, damages and liabilities that may arise in the event of any false, inaccurate or non-disclosure of any medical history and/or conditions of the Resident.</p>
        </Article>

        <Article title="4.0 Security Deposit and Service Fee">
            <SubArticle title="Security Deposit">
                <ListItem num="4.1">
                    The Customer shall upon the execution of this Agreement pay the Provider a sum as stipulated in Section 8 of Schedule 1 by way of deposit as security for the due observance and performance by the Customer of the terms and conditions of this Agreement (“Security Deposit”).
                </ListItem>
                <ListItem num="4.2">
                    The Security Deposit shall not without the prior written consent of the Provider be deemed to be or treated as payment of Service Fee and shall be maintained at all times. If the Service Fee shall at any time hereafter be increased, then the Security Deposit shall be increased proportionately and the Customer shall pay to the Provider of such additional sum as may be necessary.
                </ListItem>
                <ListItem num="4.3">
                    <>
                        The Security Deposit shall be absolutely forfeited by the Provider to pay towards the Penalty (as defined hereinafter) without prejudice to any right of action the Provider may have against the Customer in respect of any unpaid Service Fee or any antecedent breach by the Customer of the terms and conditions herein contained, in the event:-
                        <SubListItem char="a">
                            this Agreement is terminated by the Customer upon the expiry of the first (1st) month of the Term but before the expiration of the Term due to whatsoever reason; and/or
                        </SubListItem>
                        <SubListItem char="b">
                            this Agreement is terminated by the Provider as a result of the Customer's default under this Agreement.
                        </SubListItem>
                    </>
                </ListItem>
                <ListItem num="4.4">
                    Subject to Clause 4.3 above, the Security Deposit shall be refunded by the Provider to the Customer within fourteen (14) days from the expiry date of this Agreement or the termination date of this Agreement, as the case may be, provided that the Customer shall yield up the Room in accordance with the provisions of this Agreement, free of any interest and subject to such deductions therefrom as the Provider may lawfully make in accordance with the terms and conditions hereof, including but without limiting in any way the rights and remedies of the Provider, any reimbursement of all costs and expenses, losses and damages incurred, expended or suffered or to be incurred, as a result of any breach or default on the part of the Customer of their obligations and liabilities hereunder, and or of such sum or sums which may be due to the Provider and/or incurred by the Provider in the enforcement of its rights and remedies hereunder or in law or in equity.
                </ListItem>
            </SubArticle>

            <SubArticle title="Service Fee">
                <ListItem num="4.5">
                    The fee structure for the Services is stipulated in Section 1 of Schedule 2 and all fees payable by the Customer shall be collectively referred to as the “Service Fee”. The Service Fee shall be payable in the manner set out in Section 2 of Schedule 2.
                </ListItem>
                <ListItem num="4.6">
                    Notwithstanding any provisions to the contrary, the Service Fee shall be subject to fluctuations and variations and the revised amount shall be payable by the Customer upon the expiry of one (1) month's written notice from the Provider. The fluctuations and variations in the Service Fee shall be determined from time to time and at any time by the Provider at the Provider's sole and absolute discretion should there be an increase in costs and the Customer shall not raise any objection of any kind in respect thereof.
                </ListItem>
                <ListItem num="4.7">
                    The Customer hereby agrees and authorises the Provider to undertake an appraisal in relation to the needs for the level of care of the Resident as and when deemed necessary by the Provider. As circumstances and the scope of services may change due to the need of the Customer, the Provider may regularly review the Service Fee and discuss with the Customer if any such changes are reasonably required. Where the Provider is required to carry out services which fall outside the agreed scope of services as set out in Section 4 of Schedule 1, the Provider shall obtain the Customer's consent on such additional fees to be incurred for such additional services.
                </ListItem>
                <ListItem num="4.8">
                    For the avoidance of doubt, the Customer shall continue to be liable to pay the full Service Fee without discount despite the Resident's non-occupation and/or absent from the Room and/or the Premises for whatsoever period due to whatsoever reason including but not limited to hopitalisation, until and unless this Agreement is lawfully terminated and subject to the Customer yielding up to the Provider the Room in accordance with the provisions of this Agreement.
                </ListItem>
                <ListItem num="4.9">
                    All obligations of the Customer in relation to payment including but not limited to Service Fee shall survive the termination of this Agreement.
                </ListItem>
                <ListItem num="4.10">
                    The Service Fee and all other monies due and payable by the Customer under this Agreement shall be payable by the Customer to the Provider in advance not later than the seventh (7th) day of each and every month for the entire Term or within five (5) days from the date of receipt of the invoice issued by the Provider, whichever is earlier.
                </ListItem>
                <ListItem num="4.11">
                    In the event that the Service Fee or any other payment or charges payable by the Customer under this Agreement is subject to any goods and services tax or any other tax or form of charges payable to the relevant authorities imposed upon by law, the Customer shall notwithstanding anything to the contrary whether in this Agreement or otherwise, pay or reimburse the Provider for such tax or charges.
                </ListItem>
                <ListItem num="4.12">
                    The Service Fee for the first month shall be pro-rated accordingly to reflect the number of days from the Commencement Date to the last calendar date of the first month of the Term. In the event of sooner determination of this Agreement prior to the expiry of the Term, the Service Fee for the last month shall be pro-rated to reflect the number of days in the last month.
                </ListItem>
                <ListItem num="4.13">
                    Without prejudice to the Provider's rights and remedies reserved under this Agreement, in law or at equity, in the event the Service Fee or any part thereof and/or other charges and payments herein covenanted to be paid by the Customer shall remain unpaid after becoming due, the Provider shall be entitled to charge and the Customer shall pay to the Provider the late payment charges calculated on a daily basis at the rate of eight percent (8%) per annum on all moneys due and payable by the Customer to the Provider under this Agreement from the date immediately following the due date until the date of full settlement thereof (“Late Payment Charges”).
                </ListItem>
            </SubArticle>
        </Article>

        <Article title="5.0 The Surety hereby warrants, covenants and undertakes, inter alia, the following:-">
            <ListItem num="5.1">
                <>
                    The Resident hereby warrants, covenants and undertakes, inter alia, the following:-
                    <SubListItem char="a">to respect the rights of others at the Premises;</SubListItem>
                    <SubListItem char="b">to respect the needs of the community at the Premises as a whole;</SubListItem>
                    <SubListItem char="c">to respect the staff of the Provider;</SubListItem>
                    <SubListItem char="d">to care for the Resident’s own health and well-being to the best of the Resident’s ability;</SubListItem>
                    <SubListItem char="e">to inform and update the Provider of the Resident’s latest medical and health condition;</SubListItem>
                    <SubListItem char="f">to keep the Room and/or the Premises clean and tidy and not to litter or cause any part of the Premises to be unsafe or untidy;</SubListItem>
                    <SubListItem char="g">to promptly notify the Provider of any conditions that may cause injury, require repairs, or which affect the habitability of the Room and the Premises;</SubListItem>
                    <SubListItem char="h">to strictly adhere to the Rules of Residency (as defined hereinafter);</SubListItem>
                    <SubListItem char="i">not to damage or misuse the Room and/or the Premises and/or any part thereof;</SubListItem>
                    <SubListItem char="j">not to make any alterations or additions to the Room and/or the Premises;</SubListItem>
                    <SubListItem char="k">not to remove any fixtures and fittings of the Provider at the Premises;</SubListItem>
                    <SubListItem char="l">not to remove any fixtures and fittings of the Provider at the Room as set out in the list of inventories annexed as Schedule 3 (“Provider’s Fixtures and Fittings”);</SubListItem>
                    <SubListItem char="m">not to use the Room and/or the Premises and/or any part thereof in a way that is unlawful or dangerous or which could cause any cancellation, restriction or increase in the insurance premium of Provider;</SubListItem>
                    <SubListItem char="n">not to use or store on or near the Room and/or the Premises and/or any part thereof, any flammable, explosive, environmentally hazardous, or other hazardous substance;</SubListItem>
                    <SubListItem char="o">not to cause nuisance;</SubListItem>
                    <SubListItem char="p">not to allow unauthorized persons to stay over at the Room without the Provider’s prior consent;</SubListItem>
                    <SubListItem char="q">not to keep valuables in any form at the Room and/or the Premises and/or any part thereof;</SubListItem>
                    <SubListItem char="r">not to give gratuities and/or bribes to the staff of the Provider;</SubListItem>
                    <SubListItem char="s">not to engage in any illegal activity, including drug-related and/or illegal gambling activity; and</SubListItem>
                    <SubListItem char="t">not conduct or allow any guests of the Resident to conduct themselves, in a loud, boisterous, unruly, or thoughtless manner so as to disturb the rights of others to peace and quiet.</SubListItem>
                    <p className="mt-3">For the avoidance of doubt, the list set out above shall not be deemed exhaustive, and the Resident shall at all times act reasonably in compliance with all the rules determined by the Provider.</p>
                </>
            </ListItem>
        </Article>

        <Article title="6.0 Rules of Residency & Emergency Procedures">
            <SubArticle title="Rules of Residency">
                <ListItem num="6.1">
                    The Customer hereby agrees to the rules and regulations set out in the “Rules of Residency” provided to the Customer from time to time. The
                </ListItem>
            </SubArticle>
        </Article>
        
        <Article title="">
            <ListItem num="12.18">
                This Agreement shall be governed by and construed in all respects in accordance with the laws of Malaysia.
            </ListItem>
        </Article>
        
        <p className="text-center font-semibold text-slate-500 py-4">*******</p>
        <p className="text-center font-medium text-slate-500 pb-4">(-The rest of this page is intentionally left blank-)</p>
      </div>
      )}

      {!isPrintView && (
        <div className="pt-8 mt-8 border-t border-slate-200 flex justify-end">
          {NextButton}
        </div>
      )}
    </div>
  );
};

export default ServiceAgreementPage;